import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Clock, 
  ArrowLeft,
  Send,
  AlertCircle,
  Trophy,
  Target
} from 'lucide-react';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const QuizDetailContainer = styled.div`
  min-height: 100vh;
  background: var(--color-background);
  padding: var(--spacing-xl) 0;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const Header = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  margin-bottom: var(--spacing-lg);
  transition: all var(--animation-duration) ease;
  
  &:hover {
    background: var(--color-surface);
    color: var(--color-primary);
    border-color: var(--color-primary);
  }
`;

const QuizTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
`;

const QuizMeta = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-lg);
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    color: var(--color-text-muted);
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Timer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--gradient-primary);
  color: white;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  font-size: 1.125rem;
  
  &.warning {
    background: linear-gradient(135deg, #f59e0b, #d97706);
  }
  
  &.danger {
    background: linear-gradient(135deg, #ef4444, #dc2626);
  }
`;

const QuestionCard = styled(motion.div)`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  
  .question-number {
    background: var(--color-primary);
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
  }
  
  .question-type {
    color: var(--color-text-muted);
    font-size: 0.875rem;
  }
`;

const QuestionText = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-lg);
  line-height: 1.5;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const OptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: var(--color-background);
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-md);
  text-align: left;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    border-color: var(--color-primary);
    background: rgba(102, 126, 234, 0.05);
  }
  
  &.selected {
    border-color: var(--color-primary);
    background: rgba(102, 126, 234, 0.1);
  }
  
  .option-letter {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--color-border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: var(--color-text-secondary);
    flex-shrink: 0;
  }
  
  &.selected .option-letter {
    background: var(--color-primary);
    color: white;
  }
  
  .option-text {
    flex: 1;
    color: var(--color-text-primary);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-lg);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ResultModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: var(--spacing-lg);
`;

const ResultCard = styled(motion.div)`
  background: var(--color-surface);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-2xl);
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const ResultIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto var(--spacing-lg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &.success {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  &.warning {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }
  
  &.danger {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
  
  svg {
    width: 40px;
    height: 40px;
  }
`;

const ResultTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
`;

const ResultScore = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: var(--spacing-lg);
`;

const ResultActions = styled.div`
  display: flex;
  gap: var(--spacing-md);
  justify-content: center;
`;

const ActionButton = styled.button`
  padding: var(--spacing-md) var(--spacing-lg);
  border: 2px solid var(--color-primary);
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &.primary {
    background: var(--color-primary);
    color: white;
    
    &:hover {
      background: var(--color-primary-dark);
    }
  }
  
  &.secondary {
    background: transparent;
    color: var(--color-primary);
    
    &:hover {
      background: var(--color-primary);
      color: white;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const QuizDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 获取测验详情
  const { data: quiz, isLoading, error } = useQuery(
    ['quiz', id],
    async () => {
      const response = await axios.get(`/api/quiz/${id}`);
      return response.data.data;
    },
    {
      enabled: !!id,
      onSuccess: (data) => {
        if (data.timeLimit) {
          setTimeLeft(data.timeLimit * 60); // 转换为秒
        }
      }
    }
  );

  // 提交测验
  const submitMutation = useMutation(
    async (submissionData) => {
      const response = await axios.post(`/api/quiz/${id}/submit`, submissionData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setResult(data.data);
        setShowResult(true);
        setIsSubmitting(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '提交失败');
        setIsSubmitting(false);
      }
    }
  );

  const handleSubmit = useCallback(() => {
    if (!isAuthenticated) {
      toast.error('请先登录');
      navigate('/login');
      return;
    }

    if (!quiz || !Array.isArray(quiz.questions)) {
      toast.error('测验数据未就绪，请稍后重试');
      return;
    }

    const answersArray = quiz.questions.map(question => ({
      questionId: question.id,
      selectedAnswer: answers[question.id] || ''
    }));

    setIsSubmitting(true);
    submitMutation.mutate({ answers: answersArray });
  }, [answers, isAuthenticated, navigate, quiz, submitMutation]);

  // 倒计时
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmit(); // 时间到自动提交
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, handleSubmit]);

  const handleAnswerSelect = (questionId, answerId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (!timeLeft || !quiz) return '';
    const percentage = timeLeft / (quiz.timeLimit * 60);
    if (percentage <= 0.1) return 'danger';
    if (percentage <= 0.25) return 'warning';
    return '';
  };

  const getResultPercentage = () => {
    if (!result) return 0;
    return Math.max(0, Math.min(100, Math.round(Number(result.score) || 0)));
  };

  const getResultIcon = () => {
    if (!result) return null;
    const percentage = getResultPercentage();
    if (percentage >= 80) return <Trophy />;
    if (percentage >= 60) return <Target />;
    return <AlertCircle />;
  };

  const getResultClass = () => {
    if (!result) return 'success';
    const percentage = getResultPercentage();
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  if (isLoading) {
    return (
      <QuizDetailContainer>
        <Container>
          <LoadingContainer>
            <LoadingSpinner />
            <p>正在加载测验...</p>
          </LoadingContainer>
        </Container>
      </QuizDetailContainer>
    );
  }

  if (error || !quiz) {
    return (
      <QuizDetailContainer>
        <Container>
          <BackButton onClick={() => navigate('/quiz')}>
            <ArrowLeft />
            返回测验列表
          </BackButton>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>测验不存在或加载失败</p>
          </div>
        </Container>
      </QuizDetailContainer>
    );
  }

  return (
    <QuizDetailContainer>
      <Container>
        <BackButton onClick={() => navigate('/quiz')}>
          <ArrowLeft />
          返回测验列表
        </BackButton>

        <Header>
          <QuizTitle>{quiz.title}</QuizTitle>
          <QuizMeta>
            <div className="meta-item">
              <Clock />
              <span>{quiz.timeLimit}分钟</span>
            </div>
            <div className="meta-item">
              <Target />
              <span>{quiz.questions.length}题</span>
            </div>
          </QuizMeta>
          
          {timeLeft !== null && (
            <Timer className={getTimerClass()}>
              <Clock />
              剩余时间: {formatTime(timeLeft)}
            </Timer>
          )}
        </Header>

        {quiz.questions.map((question, index) => (
          <QuestionCard
            key={question.id || question._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <QuestionHeader>
              <span className="question-number">第 {index + 1} 题</span>
              <span className="question-type">{question.points}分</span>
            </QuestionHeader>
            
            <QuestionText>{question.question}</QuestionText>
            
            <OptionsContainer>
              {question.options.map((option, optionIndex) => (
                <OptionButton
                  key={option.id || optionIndex}
                  className={answers[question.id] === option.id ? 'selected' : ''}
                  onClick={() => handleAnswerSelect(question.id, option.id)}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + optionIndex)}
                  </span>
                  <span className="option-text">{option.text}</span>
                </OptionButton>
              ))}
            </OptionsContainer>
          </QuestionCard>
        ))}

        <SubmitButton
          onClick={handleSubmit}
          disabled={isSubmitting || Object.keys(answers).length === 0}
        >
          <Send />
          {isSubmitting ? '提交中...' : '提交答案'}
        </SubmitButton>

        {showResult && result && (
          <ResultModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ResultCard
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResultIcon className={getResultClass()}>
                {getResultIcon()}
              </ResultIcon>
              
              <ResultTitle>
                {result.passed ? '恭喜通过！' : '继续努力！'}
              </ResultTitle>
              
              <ResultScore>
                {getResultPercentage()}%
              </ResultScore>
              
              <p style={{ marginBottom: '2rem', color: 'var(--color-text-secondary)' }}>
                答对 {result.correctCount ?? 0} / {result.totalQuestions ?? 0} 题，获得 {result.earnedPoints ?? 0} / {result.totalPoints ?? 0} 分
              </p>
              
              <ResultActions>
                <ActionButton 
                  className="secondary"
                  onClick={() => navigate('/quiz')}
                >
                  返回测验
                </ActionButton>
                <ActionButton 
                  className="primary"
                  onClick={() => window.location.reload()}
                >
                  重新测验
                </ActionButton>
              </ResultActions>
            </ResultCard>
          </ResultModal>
        )}
      </Container>
    </QuizDetailContainer>
  );
};

export default QuizDetail;
